'use client';
import React, { useState, useEffect } from 'react';
import { Check, Zap, Star, Crown, MessageCircle, Eye, Filter, TrendingUp, Shield, Loader2, Info } from 'lucide-react';
import { createClient } from '@/lib/supabase/client';
import { jsnService } from '@/lib/supabase/jsnService';
import AppNav from '@/components/AppNav';

import SafetyAndFooter from '@/components/SafetyAndFooter';

const plans = [
  {
    id: 'flex',
    name: 'Flex Plan',
    duration: '1 Month',
    price: '$29.99',
    priceNum: 29.99,
    period: '/month',
    icon: Zap,
    description: 'Try JSN Premium for a month. Perfect for active browsing and making connections.',
    features: [
      'Unlock messaging',
      'See who liked your profile',
      'Advanced filter options',
      'Priority in search results',
      'Premium badge on profile',
    ],
    cta: 'Start with Flex',
    squareLink: 'https://square.link/u/Sb07Li26',
    popular: false,
  },
  {
    id: 'value',
    name: 'Value Plan',
    duration: '3 Months',
    price: '$79.99',
    priceNum: 79.99,
    period: '/3 months',
    savings: 'Save 11%',
    icon: Star,
    description: 'Great value for those building meaningful connections.',
    features: [
      'Everything in Flex',
      'Better search visibility',
      'Read receipts',
      'See who viewed your profile',
      'Profile boost once per month',
    ],
    cta: 'Choose Value',
    squareLink: 'https://square.link/u/x7zNAIZz',
    popular: true,
  },
  {
    id: 'best',
    name: 'Best Plan',
    duration: '6 Months',
    price: '$149.99',
    priceNum: 149.99,
    period: '/6 months',
    savings: 'Save 17%',
    icon: Crown,
    description: 'Our best value for serious, relationship-minded members.',
    features: [
      'Everything in Value',
      'Top placement in Discover',
      'Unlimited profile boosts',
      'Access to exclusive events',
      'Dedicated support channel',
    ],
    cta: 'Go Best',
    squareLink: 'https://square.link/u/5cYWVVl4',
    popular: false,
  },
  {
    id: 'elite',
    name: 'Elite Plan',
    duration: '1 Year',
    price: '$269.99',
    priceNum: 269.99,
    period: '/year',
    savings: 'Save 25%',
    icon: Crown,
    description: 'Full year of premium access for the most committed members.',
    features: [
      'Everything in Best',
      'Year-round premium access',
      'VIP profile placement',
      'Early access to new features',
      'Priority support',
    ],
    cta: 'Go Elite',
    squareLink: 'https://square.link/u/PtBfvZR4',
    popular: false,
  },
];

const benefits = [
  { icon: MessageCircle, title: 'Unlock Messaging', desc: 'Read and send unlimited messages to your matches.' },
  { icon: Eye, title: 'See Who Liked You', desc: 'Know exactly who is interested in your profile.' },
  { icon: Filter, title: 'Advanced Filters', desc: 'Filter by faith, county, intention, and more.' },
  { icon: TrendingUp, title: 'Better Visibility', desc: 'Appear higher in Discover search results.' },
  { icon: Crown, title: 'Premium Badge', desc: 'Stand out with a verified Premium status badge.' },
  { icon: Shield, title: 'Priority Support', desc: 'Get faster responses from our support team.' },
];

export default function MembershipScreen() {
  const [isPremium, setIsPremium] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      const supabase = createClient();
      const { data: { user } } = await supabase?.auth?.getUser();
      if (user) {
        // Email verification gate
        if (!user?.email_confirmed_at) {
          window.location.href = '/verify-email';
          return;
        }
        const up = await jsnService?.getUserProfileById(user?.id);
        const mp = await jsnService?.getCurrentMemberProfile();

        // ── Photo-review gate ────────────────────────────────────────────
        // Block access until the profile photo is approved and account is active.
        // Admins bypass this gate. /discover-page renders the existing
        // "Photo Pending Review" UI when photoStatus is pending.
        const isAdmin = up?.role === 'admin';
        if (!isAdmin && (mp?.photoStatus !== 'approved' || up?.accountStatus !== 'active')) {
          window.location.href = '/discover-page';
          return;
        }
        // ────────────────────────────────────────────────────────────────

        setIsPremium(up?.membershipTier === 'premium' || false);
      }
      setIsLoading(false);
    };
    load();
  }, []);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 size={24} className="animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (isPremium) {
    return (
      <div className="min-h-screen bg-background text-foreground">
        <AppNav activePage="membership" />
        <div className="pt-14 flex items-center justify-center min-h-[calc(100vh-56px)]">
          <div className="text-center space-y-4 p-8 max-w-md">
            <div className="w-20 h-20 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto">
              <Crown size={36} className="text-primary" />
            </div>
            <h2 className="text-2xl font-black text-foreground">You&apos;re Already Premium!</h2>
            <p className="text-muted-foreground text-sm">You have full access to all JSN Premium features.</p>
            <a href="/discover-page" className="btn-primary px-8 py-3 rounded-xl text-sm font-semibold inline-block">
              Back to Discover
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <AppNav activePage="membership" />
      <div className="pt-14">
        {/* Payment update notice */}
        <div className="max-w-2xl mx-auto px-4 pt-6">
          <div className="rounded-xl p-4 flex items-start gap-3 bg-green-900/20 border border-green-700/40">
            <Info size={18} style={{ color: '#A0A0A0' }} className="flex-shrink-0 mt-0.5" />
            <div className="text-xs space-y-2" style={{ color: '#A0A0A0' }}>
              <p><span className="font-semibold">Secure checkout is powered by Square.</span></p>
              <p>Select a plan below to complete your payment. Once your payment is confirmed, your account will be activated for Premium access.</p>
              <p>Available payment methods include Debit or Credit Card, Apple Pay, Google Pay, or Cash App Pay.</p>
              <p>Please use the same email address associated with your JSN account so that your activation can be processed correctly.</p>
            </div>
          </div>
        </div>

        <div className="hero-gradient py-16 text-center px-4">
          <div className="max-w-2xl mx-auto space-y-4">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-primary/40 bg-primary/10 text-sm font-medium text-primary">
              <Crown size={14} />JSN Premium
            </div>
            <h1 className="text-3xl sm:text-4xl font-black text-foreground">
              Unlock Your Full<br />
              <span className="text-gradient-red">JSN Experience</span>
            </h1>
            <p className="text-muted-foreground text-base max-w-lg mx-auto">
              Free members can browse and like. Upgrade to Premium to message, connect with matches, and build meaningful relationships with Kenyan singles across the USA.
            </p>
          </div>
        </div>

        <div className="max-w-screen-xl mx-auto px-4 lg:px-8 py-12 space-y-16">
          <div>
            <h2 className="text-xl font-bold text-foreground text-center mb-8">Choose Your Plan</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
              {plans?.map(plan => {
                const PlanIcon = plan?.icon;
                const isFlexPlan = plan?.id === 'flex';
                return (
                  <div
                    key={plan?.id}
                    className={`relative bg-card border rounded-2xl p-7 flex flex-col gap-6 transition-all duration-300 hover:-translate-y-1 ${
                      plan?.popular ? 'plan-card-popular' : 'border-border hover:border-primary/50'
                    }`}
                  >
                    {plan?.popular && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-white text-xs font-bold px-4 py-1 rounded-full">
                        Most Popular
                      </div>
                    )}
                    {plan?.savings && !isFlexPlan && (
                      <div className="absolute top-4 right-4 bg-accent/20 text-accent text-xs font-bold px-2 py-0.5 rounded-full">
                        {plan?.savings}
                      </div>
                    )}
                    <div className="space-y-3">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${plan?.popular ? 'bg-primary' : 'bg-primary/10'}`}>
                          <PlanIcon size={20} className={plan?.popular ? 'text-white' : 'text-primary'} />
                        </div>
                        <div>
                          <div className="font-bold text-foreground">{plan?.name}</div>
                          <div className="text-xs text-muted-foreground">{plan?.duration}</div>
                        </div>
                      </div>
                      <div className="flex items-baseline gap-1">
                        <span className="text-4xl font-black text-foreground">{plan?.price}</span>
                        <span className="text-muted-foreground text-sm">{plan?.period}</span>
                      </div>
                      <p className="text-sm text-muted-foreground">{plan?.description}</p>
                    </div>
                    <ul className="space-y-2.5 flex-1">
                      {plan?.features?.map(feature => (
                        <li key={`${plan?.id}-${feature?.slice(0, 20)}`} className="flex items-start gap-2.5 text-sm text-foreground">
                          <Check size={15} className="text-accent flex-shrink-0 mt-0.5" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                    {/* Square Payment Link button */}
                    <a
                      href={plan?.squareLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`w-full py-3 rounded-xl text-sm font-semibold text-center block transition-all duration-200 ${
                        plan?.popular
                          ? 'btn-primary hover:opacity-90' :'bg-primary/10 text-primary border border-primary/30 hover:bg-primary/20'
                      }`}
                    >
                      {plan?.cta}
                    </a>
                  </div>
                );
              })}
            </div>
            <p className="text-center text-muted-foreground text-sm mt-6">
              All plans are one-time payments. Secure payment processing.
            </p>
            <div className="max-w-xl mx-auto mt-4 bg-card border border-border rounded-xl p-4 text-center space-y-2">
              <p className="text-xs text-foreground font-medium">
                After payment, Premium access is activated after payment confirmation. Please use the same email address associated with your JSN account.
              </p>
              <p className="text-xs text-muted-foreground">
                Square checkout may support card, Apple Pay, Google Pay, or Cash App Pay depending on your device and availability.
              </p>
            </div>
            {/* contact support line removed — Square links handle checkout */}
          </div>

          <div>
            <h2 className="text-xl font-bold text-foreground text-center mb-8">What You Get with Premium</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 max-w-4xl mx-auto">
              {benefits?.map(benefit => {
                const BenefitIcon = benefit?.icon;
                return (
                  <div key={benefit?.title} className="bg-card border border-border rounded-xl p-5 space-y-3">
                    <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                      <BenefitIcon size={20} className="text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-foreground text-sm">{benefit?.title}</h3>
                      <p className="text-xs text-muted-foreground mt-1">{benefit?.desc}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="max-w-2xl mx-auto">
            <h2 className="text-xl font-bold text-foreground text-center mb-8">Free vs Premium</h2>
            <div className="bg-card border border-border rounded-2xl overflow-hidden">
              <div className="grid grid-cols-3 bg-muted/20 border-b border-border">
                <div className="p-4 text-sm font-semibold text-muted-foreground">Feature</div>
                <div className="p-4 text-sm font-semibold text-muted-foreground text-center">Free</div>
                <div className="p-4 text-sm font-semibold text-primary text-center flex items-center justify-center gap-1">
                  <Crown size={13} />Premium
                </div>
              </div>
              {[
                { feature: 'Browse Discover', free: true, premium: true },
                { feature: 'Like & Pass Profiles', free: true, premium: true },
                { feature: 'View Full Profiles', free: false, premium: true },
                { feature: 'See Matches', free: false, premium: true },
                { feature: 'Send Messages', free: false, premium: true },
                { feature: 'See Who Liked You', free: false, premium: true },
                { feature: 'Online Now / Newest Sort', free: false, premium: true },
                { feature: 'Block Members', free: false, premium: true },
                { feature: 'Report Members', free: false, premium: true },
              ]?.map((row, i) => (
                <div key={`row-${i}`} className="grid grid-cols-3 border-b border-border last:border-0">
                  <div className="p-4 text-sm text-foreground">{row?.feature}</div>
                  <div className="p-4 text-center">{row?.free ? <Check size={16} className="text-accent mx-auto" /> : <span className="text-muted-foreground text-xs">—</span>}</div>
                  <div className="p-4 text-center">{row?.premium ? <Check size={16} className="text-accent mx-auto" /> : <span className="text-muted-foreground text-xs">—</span>}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      <SafetyAndFooter />
    </div>
  );
}
